import { RegisterError } from '../error/RegisterError';
import { Gender, UpdateUser, User } from '../models/User';
import { logWarning } from '../utils/logger';
import * as apiUserService from './api/userService';
import { ref } from 'vue';

// Loaded lazily inside `loginWithPasskey` to keep the WebAuthn module
// graph out of every consumer's startup path — UserService is imported
// by the test setup file, and the dynamic import lets per-spec
// `vi.mock` calls land before WebAuthnService resolves.
type WebAuthnModule = typeof import('./WebAuthnService');
async function loadWebAuthn(): Promise<WebAuthnModule> {
    return await import('./WebAuthnService');
}

const storageKey = 'currentUserId';

export type RegisterPayload = {
    username: string;
    password: string;
    discordUsername?: string;
    email: string;
    preferredGender: Gender;
};

export function createUserService() {
    const currentUser = ref<User>();
    async function getCurrentUser(): Promise<User | undefined> {
        try {
            if (!currentUser.value) {
                currentUser.value = await apiUserService.refreshUser() as User;
            }
        } catch (error) {
            logWarning(error);
        }

        return currentUser.value;
    }

    function clearCurrentUser() {
        currentUser.value = undefined;
        localStorage.removeItem(storageKey);
    }

    async function login(email: string, password: string): Promise<User | undefined> {
        currentUser.value = await apiUserService.login({
            email,
            password,
        }) as User;

        if (!currentUser.value) {
            throw new Error('Invalid credentials');
        }

        localStorage.setItem(storageKey, currentUser.value.id.toString());

        return currentUser.value;
    }

    async function loginWithDiscord(): Promise<void> {
        return apiUserService.loginWithDiscord();
    }

    async function loginWithPasskey(): Promise<User> {
        const webauthn = await loadWebAuthn();
        const { user: supabaseUser } = await webauthn.loginWithPasskey();
        currentUser.value = await apiUserService.loginWithPasskey(supabaseUser) as User;

        if (!currentUser.value) {
            throw new Error('Invalid credentials');
        }

        localStorage.setItem(storageKey, currentUser.value.id.toString());

        return currentUser.value;
    }

    async function register(payload: RegisterPayload): Promise<void> {
        const isUsernameTaken = await apiUserService.areUsernamesTaken(payload.username, payload.discordUsername);

        if (isUsernameTaken) {
            throw new RegisterError('Korisničko ime ili Discord ime su zauzeti!');
        }

        await apiUserService.createUser({
            email: payload.email,
            username: payload.username,
            password: payload.password,
            preferredGender: payload.preferredGender,
        });
    }

    async function updateUser(payload: UpdateUser, isAdminEditingAnotherUser = false): Promise<User> {
        return await apiUserService.updateUser(payload, isAdminEditingAnotherUser) as User;
    }

    async function logout() : Promise<void> {
        await apiUserService.logOut();
        localStorage.removeItem(storageKey);
        currentUser.value = undefined;
    }

    async function findUserById(userId: User['id']): Promise<User | undefined> {
        return await apiUserService.findUser(userId) as User | undefined;
    }

    async function getUsers(): Promise<User[]> {
        return await apiUserService.getAllUsers() as User[];
    }

    async function sendPasswordReset(email: string): Promise<void> {
        await apiUserService.sendPasswordReset(email);
    }

    return {
        login,
        logout,
        register,
        getCurrentUser,
        clearCurrentUser,
        findUserById,
        getUsers,
        updateUser,
        sendPasswordReset,
        loginWithDiscord,
        loginWithPasskey,
    };
}

export const userService = createUserService();
